import { PropsWithChildren, ReactNode, useId, useState } from 'react';

import { CloseSign, DownIcon } from '@/lib/shared/icons';

import cn from 'clsx';

import styles from './spoiler.styles.module.scss';

interface SpoilerProps extends PropsWithChildren {
  header: ReactNode;
  isActive?: boolean;
  defaultIsActive?: boolean;
  onToggle?: () => void;
  size?: 's' | 'm';
  toggleIcon?: 'plus' | 'chevron' | null;
  theme?: 'default' | 'gray';
  borderPosition?: 'header' | 'content';
  headerClassName?: string;
  small?: boolean;
  as?: 'div' | 'dl';
}

const Spoiler = ({
  header,
  children,
  defaultIsActive,
  isActive,
  onToggle,
  size = 'm',
  toggleIcon = 'plus',
  theme = 'default',
  borderPosition = 'header',
  headerClassName,
  small,
  as,
}: SpoilerProps) => {
  const [isActiveState, setIsActiveState] = useState(Boolean(defaultIsActive));
  const resolveIsActive = isActive ?? isActiveState;
  const panelId = useId();

  const onToggleState = () => {
    setIsActiveState((prev) => !prev);
  };

  const resolveOnToggle = onToggle || onToggleState;
  const Component = as ? as : 'div';

  return (
    <Component
      className={cn(
        styles.spoiler,
        resolveIsActive && styles.isActive,
        styles[`size-${size}`],
        styles[`theme-${theme}`],
        styles[`borderType-${borderPosition}`]
      )}
    >
      <div
        className={cn(
          styles.spoiler__header,
          headerClassName,
          children ? styles['spoiler__header--cursor'] : ''
        )}
        onClick={resolveOnToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            resolveOnToggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={resolveIsActive}
        aria-controls={children ? panelId : undefined}
      >
        {header}
        {toggleIcon === 'plus' ? (
          <span aria-hidden>
            <CloseSign className={styles.spoiler__headerClose} />
          </span>
        ) : toggleIcon === 'chevron' ? (
          <span aria-hidden>
            <DownIcon
              className={cn(styles.spoiler__headerClose, styles.chevron)}
            />
          </span>
        ) : null}
      </div>
      {children && (
        <div
          id={panelId}
          className={cn(
            styles.spoiler__bodyWrap,
            small && styles['spoiler__bodyWrap--small']
          )}
          aria-hidden={!resolveIsActive}
        >
          <div className={styles.spoiler__body}>{children}</div>
        </div>
      )}
    </Component>
  );
};

Spoiler.displayName = 'Spoiler';

export default Spoiler;
